import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link } from './Link';
import { ActionArrow } from './ActionArrow';
import StoreBadges from '../../../../../shared-web/StoreBadges';
import './Announcements.css';

/**
 * What's new (2026-09-14 拍板, §4.1): a small horn at the top-left of the first
 * screen, beside the wordmark, where the canopy never reaches. It is the
 * team's one place to say something to everyone — product news, not anything
 * about the person's own Embers (those are the notebook's red numbers).
 *
 * The list is written here, in order, newest first. A red dot sits on the
 * horn while there is an item this browser has not opened; opening the horn
 * clears it (the ids seen live in localStorage — a per-browser convenience,
 * nothing the server needs to know). A new item later means a new id, and the
 * dot comes back on its own.
 */
export interface Announcement {
  id: string;
  label: string;
  title: string;
  body: string;
  link?: { to: string; label: string; log: string };
  /** The stores' badges under the words (2026-10-26). */
  apps?: boolean;
}

export const ANNOUNCEMENTS: readonly Announcement[] = Object.freeze([
  // 2026-10-15: the Terms and Privacy Policy updated (counsel's reply of 2026-10-14) — the horn is how the people
  // a text cannot reach are told; the page says what changed and takes an answer (/legal/update).
  {
    id: 'terms-2026-10-15',
    label: 'Update',
    title: 'Our Terms and Privacy Policy, updated',
    body: 'A few changes, in plain words — take a look when you have a minute.',
    link: { to: '/legal/update', label: 'See what changed', log: 'announcement_legal_update' },
  },
  // 2026-10-12: the app is in both stores — the September promise kept, said here as it said it would be.
  // 2026-10-22: the stores' name is "Ember: Talk It Out" (the old one was not found by search on Google Play).
  {
    id: 'app-here-2026-10',
    label: 'New',
    title: 'Ember is on your phone',
    body: 'Find “Ember: Talk It Out” on the App Store or Google Play. Sign in with the same phone number, and your conversation is right there.',
    apps: true,
  },
  {
    id: 'talk-to-us-2026-09-15',
    label: 'We’d love to hear from you',
    title: 'Up for a longer conversation?',
    body: 'If you’re open to spending about 30 minutes with us, let us know. We regularly talk with people who use Ember so we can understand what’s working, what isn’t, and what you wish it could do.',
    link: { to: '/feedback', label: 'Let us know', log: 'announcement_feedback' },
  },
]);

const SEEN_KEY = 'ember.announcements.seen';

function readSeen(): string[] {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writeSeen(ids: string[]): void {
  try { window.localStorage.setItem(SEEN_KEY, JSON.stringify(ids)); } catch { /* private mode: the dot simply stays */ }
}

export function Announcements(): ReactElement {
  const [open, setOpen] = useState(false);
  // Unseen is computed on the client after mount: SSR has no localStorage,
  // and the first paint must match the server's (no dot) to avoid a flash.
  const [unseen, setUnseen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const seen = new Set(readSeen());
    setUnseen(ANNOUNCEMENTS.some((a) => !seen.has(a.id)));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeFromOutside = (event: PointerEvent): void => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeFromKeyboard = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromKeyboard);
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromKeyboard);
    };
  }, [open]);

  // Open, the list lifts the home nav it sits in above the composer docked at the bottom (TreeHome.css): both are in
  // .ember-main's stacking, the nav at 2 and the docked composer at 30, so the list's last link ("Let us know") lay
  // under the field and a tap on it focused the field (release check 2026-10-27, 375×667). A data attribute rather
  // than a class: React rewrites the nav's className when it docks. A page without that nav is left as it is.
  useEffect(() => {
    if (!open) return undefined;
    const nav = ref.current?.closest('.tree-home-nav');
    nav?.setAttribute('data-announcing', '');
    return () => nav?.removeAttribute('data-announcing');
  }, [open]);

  const toggle = (): void => {
    if (!open) {
      writeSeen(ANNOUNCEMENTS.map((a) => a.id));
      setUnseen(false);
    }
    setOpen((v) => !v);
  };

  return (
    <div className="announcements" ref={ref}>
      <button
        type="button"
        className={`announcements-button${unseen ? ' is-unseen' : ''}`}
        aria-label={open ? 'Close what’s new' : unseen ? 'What’s new (unread)' : 'What’s new'}
        aria-expanded={open}
        aria-controls="announcements-popover"
        data-log="nav_announcements"
        data-log-state={open ? 'close' : 'open'}
        onClick={toggle}
      >
        <HornGlyph />
        {unseen ? <span className="announcements-dot" aria-hidden /> : null}
      </button>
      {open ? (
        <div id="announcements-popover" className="announcements-popover" role="dialog" aria-label="What’s new">
          <p className="announcements-heading">What’s new</p>
          {ANNOUNCEMENTS.map((a) => (
            <article key={a.id} className="announcements-item">
              <p className="announcements-label">{a.label}</p>
              <h3 className="announcements-title">{a.title}</h3>
              <p className="announcements-body">{a.body}</p>
              {a.apps ? <StoreBadges discovery="horn_list" className="announcements-apps" /> : null}
              {a.link ? (
                <Link to={a.link.to} className="announcements-link ember-text-action" data-log={a.link.log} onClick={() => setOpen(false)}>
                  <span>{a.link.label}</span>
                  <ActionArrow />
                </Link>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** A small horn in the canopy's own dim light: a translucent pale-gold bell
    (the colour of the lights in the leaves), no bright rim, the sound drawn as
    two soft arcs — an ornament of the night scene, not a control from the nav
    and not a second flame beside the wordmark. */
function HornGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 28 28" aria-hidden>
      <defs>
        <linearGradient id="horn-bell" x1="4" y1="6" x2="17" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fbe7c0" stopOpacity="0.9" />
          <stop offset="0.6" stopColor="#ecc07e" stopOpacity="0.8" />
          <stop offset="1" stopColor="#c98b45" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      {/* bell */}
      <path d="M4 12.2v3.6c0 1 .8 1.8 1.8 1.8h2.4l7.6 4.6c.6.4 1.4-.1 1.4-.8V6.6c0-.7-.8-1.2-1.4-.8L8.2 10.4H5.8c-1 0-1.8.8-1.8 1.8Z" fill="url(#horn-bell)" />
      {/* handle */}
      <path d="M8.4 17.6 9.6 23h3.1l-1-5.2" fill="#c98b45" fillOpacity="0.8" />
      {/* sound */}
      <path d="M20.6 10.6a4.6 4.6 0 0 1 0 6.8" fill="none" stroke="#ecc07e" strokeWidth="1.6" strokeLinecap="round" opacity="0.75" />
      <path d="M23.4 7.8a8.4 8.4 0 0 1 0 12.4" fill="none" stroke="#ecc07e" strokeWidth="1.6" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}
